import type { FleetColumn, FleetView, NodeStatus } from "./domain";

const statuses = [
  "healthy",
  "warning",
  "critical",
  "stale",
  "offline",
] as const satisfies readonly NodeStatus[];
const columns = [
  "status",
  "name",
  "project",
  "location",
  "release",
  "disk",
  "memory",
  "backup",
  "update",
  "last-seen",
] as const satisfies readonly FleetColumn[];

/** Parsed and boundary-validated fleet-view query values. */
export interface FleetQueryState {
  readonly query: string;
  readonly statuses: readonly NodeStatus[];
  readonly tags: readonly string[];
  readonly projectIds: readonly string[];
  readonly locations: readonly string[];
  readonly releases: readonly string[];
  readonly capabilities: readonly string[];
  readonly columns: readonly FleetColumn[];
  readonly groupBy: FleetView["groupBy"];
  readonly sort: FleetView["sort"];
  readonly maxAge: string;
}

function list(parameters: URLSearchParams, name: string): string[] {
  return parameters.getAll(name).flatMap((value) => value.split(",")).map((
    value,
  ) => value.trim()).filter(Boolean);
}

/** Decode the closed fleet-view URL grammar without evaluating arbitrary expressions. */
export function decodeFleetQuery(search: string): FleetQueryState {
  const parameters = new URLSearchParams(search);
  const requestedSort = parameters.get("sort");
  const requestedGroup = parameters.get("group");
  return {
    query: parameters.get("q") ?? "",
    statuses: list(parameters, "status").filter((value): value is NodeStatus =>
      statuses.includes(value as NodeStatus)
    ),
    tags: list(parameters, "tags"),
    projectIds: list(parameters, "project"),
    locations: list(parameters, "location"),
    releases: list(parameters, "release"),
    capabilities: list(parameters, "capability"),
    columns: list(parameters, "columns").filter((value): value is FleetColumn =>
      columns.includes(value as FleetColumn)
    ),
    groupBy: requestedGroup === "project" || requestedGroup === "status" ||
        requestedGroup === "location"
      ? requestedGroup
      : "none",
    sort: requestedSort === "status" || requestedSort === "last-seen"
      ? requestedSort
      : "name",
    maxAge: parameters.get("maxAge") ?? "",
  };
}

/** Encode every saved filter and presentation field into one canonical URL. */
export function encodeFleetView(view: FleetView): string {
  const parameters = new URLSearchParams();
  if (view.filter.query) parameters.set("q", view.filter.query);
  if (view.filter.statuses.length) {
    parameters.set("status", view.filter.statuses.join(","));
  }
  if (view.filter.tags.length) {
    parameters.set("tags", view.filter.tags.join(","));
  }
  if (view.filter.projectIds?.length) {
    parameters.set("project", view.filter.projectIds.join(","));
  }
  if (view.filter.locations?.length) {
    parameters.set("location", view.filter.locations.join(","));
  }
  if (view.filter.releases?.length) {
    parameters.set("release", view.filter.releases.join(","));
  }
  if (view.filter.capabilities?.length) {
    parameters.set("capability", view.filter.capabilities.join(","));
  }
  if (
    view.filter.maxContactAgeMinutes !== null &&
    view.filter.maxContactAgeMinutes !== undefined
  ) parameters.set("maxAge", String(view.filter.maxContactAgeMinutes));
  if (view.sort !== "name") parameters.set("sort", view.sort);
  parameters.set("columns", view.columns.join(","));
  if (view.groupBy !== "none") parameters.set("group", view.groupBy);
  return `/?${parameters.toString()}`;
}
